import React, { useEffect, useState } from 'react';
import { updateCoordinator } from '../../pwa/updateCoordinator';
import { Button } from '../../ui/primitives/Button';
import { Sparkles, RefreshCw, X } from 'lucide-react';

export const UpdateNotification: React.FC = () => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [isReloading, setIsReloading] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [reloadHandler, setReloadHandler] = useState<(() => Promise<void>) | null>(null);

  useEffect(() => {
    const unsubscribe = updateCoordinator.onUpdateAvailable((reloadPage) => {
      setReloadHandler(() => reloadPage);
      setUpdateAvailable(true);
      setDismissed(false);
    });

    return () => {
      unsubscribe();
    };
  }, []);

  if (!updateAvailable || dismissed) {
    return null;
  }

  const handleUpdate = async () => {
    if (!reloadHandler) return;
    setIsReloading(true);
    try {
      await reloadHandler();
    } catch (err) {
      console.error('[UpdateNotification] Failed to reload page:', err);
      setIsReloading(false);
    }
  };

  return (
    <aside
      aria-label="Software Update"
      role="status"
      className="w-full bg-forge-blue/10 border-b border-forge-blue/30 px-4 py-2.5 sm:px-6 transition-all animate-in slide-in-from-top-2 duration-200"
    >
      <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
        <div className="flex items-center gap-2.5 text-foreground-primary text-sm font-medium">
          <div className="w-6 h-6 rounded-full bg-forge-blue/20 text-forge-blue flex items-center justify-center shrink-0">
            <Sparkles className="w-3.5 h-3.5" />
          </div>
          <span>
            A new version of CallFoundry is available with performance and scenario enhancements.
          </span>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <Button
            size="sm"
            variant="primary"
            isLoading={isReloading}
            leftIcon={<RefreshCw className={`w-3.5 h-3.5 ${isReloading ? 'animate-spin' : ''}`} />}
            onClick={handleUpdate}
          >
            Update Now
          </Button>
          <button
            type="button"
            onClick={() => setDismissed(true)}
            className="p-1 rounded-md text-foreground-muted hover:text-foreground-primary hover:bg-surface-2 transition-colors focus-visible:ring-2 focus-visible:ring-border-focus"
            aria-label="Dismiss update notification"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </aside>
  );
};
